import * as SideMenuOptions from '@/components/sidemenu/side-menu-options'
import * as DashboardWidgets from '@/components/dashboard/dashboard-widgets'
import { progressSectionType } from '@/components/dashboard/progress-sections'
import { brokerageRoles, insuredRolesList } from '@/mixins/role-codes'
import * as claimsUtil from '@/components/claims/claims-util'

// Javascript is weird
function objectIsEmpty(obj) {
  for (var i in obj) return false
  return true
}

const getGlobalAlert = state => {
  return state.alert
}

const getGlobalErrorAlert = state => {
  return state.errorAlert
}

const getGlobalInfo = state => {
  return state.info
}

const getLoading = state => {
  return state.loading
}

const getUser = state => {
  return state.user
}

const getUsername = state => {
  return state.user && state.user.username
}

const isLoggedIn = state => {
  return state.user && state.user.username
}

const isAnAdmin = state => {
  return state.user && state.user.isAdmin
}

const isViewerUser = state => {
  return state.user && state.user.isViewer
}

const isTfaValidated = state => {
  if (state.user == null) {
    return false
  }
  if (state.user.tfaEnabled == null || state.user.tfaValidated == null) {
    return false
  }
  return !state.user.tfaEnabled || (state.user.tfaEnabled && state.user.tfaValidated)
}

const getActiveAccount = state => {
  return state.activeAccount
}
const getActiveAccountAmsType = state => {
  const activeAccount = state.activeAccount || {}
  return activeAccount.amsType
}
const getActiveAccountUsers = state => {
  const activeAccount = state.activeAccount || {}
  return activeAccount.users
}
const getActiveAccountAccountUsers = state => {
  const activeAccount = state.activeAccount || {}
  const accountUsers = activeAccount.accountUsers
  return accountUsers
}
const getActiveAccountInsuredUsers = (state, getters) => {
  const accountUsers = getters.getActiveAccountAccountUsers
  if (accountUsers == null) {
    return []
  }
  const allUsers = Object.values(accountUsers)
  return allUsers.filter(u => {
    const role = u.roleCode
    return insuredRolesList.includes(role)
  })
}
const getActiveAccountBrokerageCode = state => {
  const activeAccount = state.activeAccount || {}
  return activeAccount.brokerCode
}
const getActiveAccountBrokerageName = state => {
  const activeAccount = state.activeAccount || {}
  return activeAccount.brokerName
}
const getActiveAccountBrokerageAccountStatus = state => {
  const activeAccount = state.activeAccount || {}
  return activeAccount.brokerAccountStatus
}
const getActiveAccountBrokerageRelationship = state => {
  const activeAccount = state.activeAccount || {}
  return activeAccount.brokerRelationship
}

const getActiveAccountPrimaryBrokerContact = state => {
  const activeAccount = state.activeAccount || {}
  return activeAccount.primaryBrokerContact
}
const getActiveAccountRole = (state, getters) => {
  const accountCode = state.activeAccount && state.activeAccount.accountCode
  return getters.getAccountRoles[accountCode]
}
const getActiveAccountPrimaryEffDate = state => {
  const activeAccount = state.activeAccount || {}
  return activeAccount.primaryEffectiveDate
}
const getActiveAccountPhase = state => {
  let phase = undefined
  if (state.activeAccount) {
    phase = 'Setup'
    if (state.activeAccount.phase) {
      phase = state.activeAccount.phase
    }
  }

  return phase
}

// True If: Global Admin
// True If: In any brokerage (broker users)
// True If: Is assigned a broker role in the active account (account users)
const isBrokerageUser = (state, getters) => {
  if (getters.isAnAdmin) {
    return true
  }
  const brokerages = getters.getBrokerages
  if (brokerages != null && Object.keys(brokerages).length > 0) {
    return true
  }

  const activeAccount = getters.getActiveAccount
  if (activeAccount?.accountCode) {
    const role = getters.getActiveAccountRole
    const isAssignedAsBrokerageRole = brokerageRoles.includes(role)

    if (isAssignedAsBrokerageRole) {
      return true
    }
  }
  // If none of the above, false
  return false
}

const getAuthedAccounts = state => {
  return Object.keys(state.accountRoles)
}
const getAccountRoles = state => {
  return state.accountRoles
}
const getAccountTasks = state => {
  return state.accountTasks
}
const getAccountNotifications = state => {
  return state.accountNotifications
}
const getBrokerRoles = state => {
  return state.brokerRoles
}
const getBrokerages = state => {
  return state.brokerages
}
const getBrokerageCodes = state => {
  if (state.brokerages) {
    return Object.keys(state.brokerages)
  }
  return []
}
const isBrokerageAdmin = state => {
  const roles = Object.values(state.brokerRoles)
  return roles.some(r => r === 'ADMIN' || r === 'SUPER_ADMIN')
}
const getDashboard = state => {
  return state.dashboard
}
const showCodeSplittingRefresh = state => {
  return state.showCodeSplittingRefresh
}
const getAppCycleSections = state => {
  return state.dashboard.applicationCycle.sections
}
const isReadyForSubmission = (state, getters) => {
  const getSectionData = sectionName => {
    return state.dashboard.applicationCycle.sections[sectionName] || {}
  }

  const sectionIsComplete = sectionName => getSectionData(sectionName).isComplete

  let itemsBeforeSubmission = getters.sectionsBeforeSubmission

  return itemsBeforeSubmission.every(sectionIsComplete)
}

const getIntervalName = state => {
  return state.tokenIntervalName
}

const getPreferences = state => state.preferences

const getPreference = state => name => state.preferences.find(p => p.name === name)

const getAccountPreferenceValue = state => preferenceName => {
  const preferenceValue = state.preferences.find(p => p.name === preferenceName)
  return preferenceValue?.account?.value
}
const getBrokeragePreferenceValue = state => preferenceName => {
  const preferenceValue = state.preferences.find(p => p.name === preferenceName)
  return preferenceValue?.brokerage?.value
}

function getPreferenceValue(state, getters, preferenceName, defaultState) {
  if (!state.preferences || objectIsEmpty(state.preferences)) {
    return defaultState
  }

  const prefObj = getters.getPreference(preferenceName)
  if (objectIsEmpty(prefObj)) {
    return defaultState
  }
  if (prefObj.account && prefObj.account.value != null) {
    return prefObj.account.value
  } else if (prefObj.brokerage && prefObj.brokerage.value != null) {
    return prefObj.brokerage.value
  }
  return defaultState
}

const preference_showLossRuns = (state, getters) => {
  const preferenceName = 'LOSS_RUNS'
  const defaultState = false
  return getPreferenceValue(state, getters, preferenceName, defaultState)
}

const getFeatures = state => state.featureFlags

function getFeatureValue(state, flagName, defaultState) {
  if (!state.featureFlags || objectIsEmpty(state.featureFlags)) {
    return defaultState
  }
  if (objectIsEmpty(state.featureFlags[flagName])) {
    return defaultState
  }
  return state.featureFlags[flagName].value
}

const feature_showTelecom = state => {
  const flagName = 'TELECOM_QUESTIONS'
  const defaultState = false
  return getFeatureValue(state, flagName, defaultState)
}

const quoteFlowName = (state, getters) => {
  const relationship = getters.getActiveAccountBrokerageRelationship
  const isProspect = 'PROSPECT' === relationship
  if (isProspect) {
    return getters.feature_proposalWorkflowName
  } else {
    return getters.feature_renewalWorkflowName
  }
}

const feature_proposalWorkflowName = (state, getters) => {
  const flagName = 'PROPOSAL_FLOW_NAME'
  const defaultState = getters.feature_renewalWorkflowName
  return getFeatureValue(state, flagName, defaultState)
}

const feature_renewalWorkflowName = state => {
  const flagName = 'RENEWAL_FLOW_NAME'
  const defaultState = 'Renewal Dashboard'
  return getFeatureValue(state, flagName, defaultState)
}
const feature_pendingScheduleView = state => {
  const flagName = 'PENDING_SCHEDULE_VIEW'
  const defaultState = false
  return getFeatureValue(state, flagName, defaultState)
}
const feature_scheduleSnapshotView = state => {
  const flagName = 'SCHEDULE_SNAPSHOT_VIEW'
  const defaultState = false
  return getFeatureValue(state, flagName, defaultState)
}

const feature_showPdfEditing = state => {
  const flagName = 'APPLICATION_PDF_EDITING'
  const defaultState = false
  return getFeatureValue(state, flagName, defaultState)
}

const feature_submitOnlyToCurrentManagers = state => {
  const flagName = 'SUBMIT_ONLY_TO_CURRENT_MANAGERS'
  const defaultState = true
  return getFeatureValue(state, flagName, defaultState)
}

const sectionsBeforeSubmission = (state, getters) => {
  let enabledRenewalDashboard = getters.renewalOptionsOnAccount

  const indexOfSubmission = enabledRenewalDashboard.indexOf(progressSectionType.SUBMISSION)
  if (indexOfSubmission === -1) {
    // Submission is assumed to be at the end
    // This logic will be obsolete soon
    return enabledRenewalDashboard
  } else {
    const itemsBeforeSubmission = enabledRenewalDashboard.slice(0, indexOfSubmission)
    return itemsBeforeSubmission
  }
}

/**
 * Consider using: renewalOptionsOnAccount
 *
 * This defines what is allowable for the broker/account
 *  - but not necessarily what is configured
 */
const feature_renewal_options = state => {
  const flagName = 'RENEWAL_DASHBOARD_FEATURES'
  const defaultState = [
    progressSectionType.PROFILE,
    progressSectionType.SCHEDULES,
    progressSectionType.APPLICATIONS,
    progressSectionType.LOSS_RUNS,
    progressSectionType.MOD,
    progressSectionType.FINANCIALS,
  ]
  return getFeatureValue(state, flagName, defaultState)
}

const renewalOptionsOnAccount = (state, getters) => {
  let options = getters.feature_renewal_options
  if (!getters.preference_showLossRuns) {
    options = options.filter(e => e !== progressSectionType.LOSS_RUNS)
  }

  return options
}

const hasRenewalOption = (state, getters) => option => {
  const options = getters.feature_renewal_options
  if (!options || options.length === 0) {
    return false
  }

  return options.includes(option)
}
const feature_side_menu_options = state => {
  const flagName = 'SIDE_MENU_FEATURES'
  const defaultState = SideMenuOptions.defaultOptions
  return getFeatureValue(state, flagName, defaultState)
}

const hasSideMenuOption = (state, getters) => option => {
  const options = getters.feature_side_menu_options
  if (!options || options.length === 0) {
    return false
  }

  return options.includes(option)
}

const feature_side_menu_name_override = state => {
  const flagName = 'SIDE_MENU_NAME_OVERRIDE'
  if (state.featureFlags && state.featureFlags[flagName]) {
    const value = state.featureFlags[flagName]?.value
    return value
  } else {
    return undefined
  }
}

const feature_main_card_name_override = state => {
  const flagName = 'MAIN_CARD_NAME_OVERRIDE'
  return getFeatureValue(state, flagName, undefined)
}
const getSideMenuNameOverride = (state, getters) => option => {
  const options = getters.feature_side_menu_name_override
  if (!options || Object.keys(options).length === 0) {
    return undefined
  }

  return options[option]
}
const getMainCardNameOverride = (state, getters) => option => {
  const options = getters.feature_main_card_name_override
  if (!options || Object.keys(options).length === 0) {
    return undefined
  }

  return options[option]
}

const hasPolicyPage = (state, getters) => option => {
  const options = getters.feature_policy_pages
  if (!options || options.length === 0) {
    return false
  }

  return options.includes(option)
}
const feature_claim_types = state => {
  const flagName = 'CLAIM_TYPES_TO_SHOW'
  const defaultState = claimsUtil.DEFAULT_CLAIM_TYPES
  return getFeatureValue(state, flagName, defaultState)
}
const feature_policy_pages = state => {
  const flagName = 'POLICY_ADMIN_PAGES'
  const defaultState = ['POLICIES', 'PREMIUM_SUMMARY', 'ENDORSEMENTS', 'PROPOSALS']
  return getFeatureValue(state, flagName, defaultState)
}
const feature_broker_graphs = state => {
  const flagName = 'BROKER_GRAPHS'
  const defaultState = ['ACCOUNT_INFORMATION', 'PROPERTY_INFORMATION']
  return getFeatureValue(state, flagName, defaultState)
}

const feature_account_dashboard_widgets = state => {
  const flagName = 'ACCOUNT_DASHBOARD_WIDGETS'
  const defaultState = DashboardWidgets.defaultOptions
  return getFeatureValue(state, flagName, defaultState)
}

const feature_showChildProgressBar = state => {
  const flagName = 'SHOW_CHILD_PROGRESS'
  const defaultState = true
  return getFeatureValue(state, flagName, defaultState)
}

const feature_showAuditButton = state => {
  const flagName = 'SHOW_AUDIT_BUTTON'
  const defaultState = false
  return getFeatureValue(state, flagName, defaultState)
}

const feature_showLinks = state => {
  const flagName = 'LINKS'
  const defaultState = false
  return getFeatureValue(state, flagName, defaultState)
}

const feature_showBrDocs = state => {
  const flagName = 'BR_DOCS'
  const defaultState = false
  return getFeatureValue(state, flagName, defaultState)
}

const feature_showBankDocs = state => {
  const flagName = 'BANK_DOCS'
  const defaultState = false
  return getFeatureValue(state, flagName, defaultState)
}

const feature_showBrokerAppLibrary = state => {
  const flagName = 'SHOW_BROKER_APP_LIBRARY'
  const defaultState = false
  return getFeatureValue(state, flagName, defaultState)
}

const feature_showBorButton = state => {
  const flagName = 'SHOW_BOR_BUTTON'
  const defaultState = false
  return getFeatureValue(state, flagName, defaultState)
}

const feature_useStagingFlow = state => {
  const flagName = 'USE_STAGING_FLOW'
  const defaultState = false
  return getFeatureValue(state, flagName, defaultState)
}

const feature_accountNoticeConfig = state => {
  const flagName = 'ACCOUNT_NOTICE_CONFIG'
  const defaultState = false
  return getFeatureValue(state, flagName, defaultState)
}

const feature_showContactCard = state => {
  const flagName = 'SHOW_PRIMARY_CONTACT_CARD'
  const defaultState = false
  return getFeatureValue(state, flagName, defaultState)
}

const feature_showFloodRisk = state => {
  const flagName = 'SHOW_FLOOD_RISK'
  const defaultState = false
  return getFeatureValue(state, flagName, defaultState)
}

const feature_showScheduleBackdates = state => {
  const flagName = 'SCHEDULE_BACKDATES'
  const defaultState = false
  return getFeatureValue(state, flagName, defaultState)
}

const feature_showMultipleScheduleTabs = state => {
  const flagName = 'SCHEDULE_MULTIPLE_TABS'
  const defaultState = false
  return getFeatureValue(state, flagName, defaultState)
}

const feature_scheduleClassificationType = state => {
  const flagName = 'SCHEDULE_CLASSIFICATION_TYPE'
  const defaultState = false
  return getFeatureValue(state, flagName, defaultState)
}


const feature_showGlobalPolicyManagement = state => {
  const flagName = 'SHOW_GLOBAL_POLICY_MANAGEMENT'
  const defaultState = false
  return getFeatureValue(state, flagName, defaultState)
}

const feature_generateAutoIdCards = state => {
  const flagName = 'SHOW_GENERATE_AUTO_ID_CARDS'
  const defaultState = false
  return getFeatureValue(state, flagName, defaultState)
}

const areFeaturesLoaded = state => {
  return state.featuresLoaded
}

const hasPermission = (state, getters) => perm => {
  if (getters.isAnAdmin || perm === 'any') {
    return true
  }
  if (getters.isBrokerageUser) {
    return true
  }

  if (state.activeAccount && state.activeAccount.permissions) {
    return state.activeAccount.permissions.includes(perm)
  }
  return false
}

const getAuth = state => {
  return state.auth
}

const getFormModel = state => {
  return state.formModel
}

const getRedirectUrl = state => {
  return state.redirectUrl
}

export default {
  getActiveAccount,
  getAccountRoles,
  getAccountTasks,
  getAccountNotifications,
  getGlobalAlert,
  getGlobalErrorAlert,
  getGlobalInfo,
  getLoading,
  getUser,
  getUsername,
  isLoggedIn,
  getAuthedAccounts,
  getActiveAccountUsers,
  getActiveAccountAccountUsers,
  getActiveAccountInsuredUsers,
  getActiveAccountPrimaryEffDate,
  getActiveAccountBrokerageCode,
  getActiveAccountBrokerageName,
  getActiveAccountBrokerageAccountStatus,
  getActiveAccountBrokerageRelationship,
  getActiveAccountPrimaryBrokerContact,
  getActiveAccountAmsType,
  getActiveAccountPhase,
  getAppCycleSections,
  getDashboard,
  getActiveAccountRole,
  isBrokerageUser,
  showCodeSplittingRefresh,
  isReadyForSubmission,
  isAnAdmin,
  isViewerUser,
  getIntervalName,
  getBrokerages,
  getBrokerageCodes,
  getBrokerRoles,
  isBrokerageAdmin,
  preference_showLossRuns,
  quoteFlowName,
  feature_proposalWorkflowName,
  feature_renewalWorkflowName,
  feature_showTelecom,
  feature_pendingScheduleView,
  feature_scheduleSnapshotView,
  feature_submitOnlyToCurrentManagers,
  feature_showPdfEditing,
  feature_renewal_options,
  feature_side_menu_options,
  feature_claim_types,
  feature_policy_pages,
  feature_broker_graphs,
  feature_showLinks,
  feature_account_dashboard_widgets,
  feature_showChildProgressBar,
  feature_showAuditButton,
  feature_showBrDocs,
  feature_showBankDocs,
  feature_showBrokerAppLibrary,
  feature_showBorButton,
  feature_useStagingFlow,
  feature_accountNoticeConfig,
  feature_showContactCard,
  feature_showFloodRisk,
  feature_showScheduleBackdates,
  feature_showMultipleScheduleTabs,
  feature_scheduleClassificationType,
  feature_side_menu_name_override,
  feature_showGlobalPolicyManagement,
  feature_generateAutoIdCards,
  getSideMenuNameOverride,
  feature_main_card_name_override,
  getMainCardNameOverride,
  renewalOptionsOnAccount,
  sectionsBeforeSubmission,
  getFeatures,
  getPreferences,
  getPreference,
  getAccountPreferenceValue,
  getBrokeragePreferenceValue,
  isTfaValidated,
  hasRenewalOption,
  hasSideMenuOption,
  hasPermission,
  hasPolicyPage,
  areFeaturesLoaded,
  getAuth,
  getFormModel,
  getRedirectUrl,
}
